import { memo, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { Transition } from '@headlessui/react';
import { LanguageOption } from '@goldcast/player-config/src/types/clip-types';
import { isSpeakerLayout } from '@goldcast/player-config/src/utils/layout';
import { getIsDuplicateRecording, isSplitsInTimelineEnabledPure } from '@goldcast/player-config/src/utils/clipUtils';
import { CustomizerMenuType, STANDARD_PANEL_MENU_TYPES } from './constants';
import ClipCustomizerCaptionsPopup from './ClipCustomizerCaptionsPopup';
import ClipCustomizerTemplatesPopup from './ClipCustomizerTemplatesPopup';
import ClipCustomizerBackgroundPopup from './ClipCustomizerBackgroundPopup';
import ClipCustomizerSpeakersPopup from './Speakers/ClipCustomizerSpeakersPopup';
import ClipCustomizerImagesPopup from './ClipCustomizerImagesPopup';
import ClipCustomizerVideoPopup from './ClipCustomizerVideoPopup';
import ClipCustomizerAudioPopup from './ClipCustomizerAudioPopup';
import ClipCustomizerOverlaysPopup from './ClipCustomizerOverlaysPopup/ClipCustomizerOverlaysPopup';
import ClipCustomizerTemplatesPopupRevamp from './ClipCustomizerTemplatesPopupRevamp';
import ClipCustomizerVersioningPanel from './ClipCustomizerVersioningPanel/ClipCustomizerVersioningPanel';
import { timelineActionsStore } from '../ClipNewTimeline/store/timelineActions';
import useClipCustomizerWarnings from './useClipCustomizerWarnings';
import ClipCustomizerLayoutPopupV2 from '@/Pages/Clip/ClipPlayer/ClipCustomizerV2/Layout/ClipCustomizerLayoutPopup';
import ClipCustomizerNewLayoutPopup from '@/Pages/Clip/ClipPlayer/ClipCustomizerV2/Layout/NewLayout/ClipCustomizerNewLayoutPopup';
import ActionButtons, { ActionButton } from '@/components/atoms/ActionButtons/ActionButtons';
import DynamicPopup from '@/components/atoms/DynamicPopup/DynamicPopup';
import { isAnyDialogOpened } from '@/Pages/Sessions/uiComponents/ContentDialog/utils';
import { FeatureFlagKeys } from '@/services/featureFlag';
import featureFlagStore from '@/stores/featureFlagStore';
import CaptionLanguageSelectorPopup from '@/Pages/Clip/CaptionTranslation/CaptionLanguageSelectorPopup';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import useTranslationState from '@/hooks/useTranslationState';
import { UnsupportedLanguageWarningModal } from '@/components/molecules/UnsupportedLanguageWarningModal/UnsupportedLanguageWarningModal';
import { isLayoutsWithVisionDataEnabled } from '@/stores/clip';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { useRestrictedFeature } from '@/hooks/useGlobalRestrictionDialog';
import useEditorRestrictions from '@/hooks/useEditorRestrictions';

function ClipCustomizerMenu({
  isFullRecordingEdit = false,
  onTimeUpdate,
  useNewRecordingsPlayer
}: {
  isFullRecordingEdit?: boolean;
  onTimeUpdate: (time: number, shouldPlay?: boolean) => void;
  useNewRecordingsPlayer?: boolean;
}) {
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const { isAddingImage, isAddingVideo, isAddingAudio } = useSyncExternalStore(
    timelineActionsStore.subscribe,
    timelineActionsStore.getSnapshot
  );
  const { clipId, clipData } = useClipsContext();
  const clipCustomizerWarnings = useClipCustomizerWarnings();

  const {
    isTranslationProcessing,
    allowCaptionTranslation,
    onCaptionTranslationConfirm,
    handleCaptionTranslationRestricted,
    isOriginalLanguageSupportedForTranslation,
    checkLanguageAndExecute,
    isWarningModalOpen,
    closeWarningModal
  } = useTranslationState(clipData);

  const { handleAction } = useRestrictedFeature(
    'captionTranslation',
    allowCaptionTranslation,
    () => checkLanguageAndExecute(() => openPopupMenu(CustomizerMenuType.Translation)),
    {
      reason: 'plan',
      onSecondaryAction: handleCaptionTranslationRestricted
    }
  );

  const { isBlocked } = useEditorRestrictions();

  const [isMenuPopupOpened, setIsMenuPopupOpened] = useState<CustomizerMenuType | null>(null);
  const [overlaysInitialTab, setOverlaysInitialTab] = useState<'video' | 'image'>('video');

  // Create refs for each button
  const promptOverrideRef = useRef<HTMLButtonElement>(null);
  const translationRef = useRef<HTMLButtonElement>(null);
  const speakersRef = useRef<HTMLButtonElement>(null);
  const templatesRef = useRef<HTMLButtonElement>(null);
  const layoutRef = useRef<HTMLButtonElement>(null);
  const captionsRef = useRef<HTMLButtonElement>(null);
  const backgroundRef = useRef<HTMLButtonElement>(null);
  const imagesRef = useRef<HTMLButtonElement>(null);
  const videosRef = useRef<HTMLButtonElement>(null);
  const audioRef = useRef<HTMLButtonElement>(null);
  const overlaysRef = useRef<HTMLButtonElement>(null);
  const versioningRef = useRef<HTMLButtonElement>(null);

  const buttonRefs = useMemo(
    () => ({
      [CustomizerMenuType.PromptOverride]: promptOverrideRef,
      [CustomizerMenuType.Translation]: translationRef,
      [CustomizerMenuType.Speakers]: speakersRef,
      [CustomizerMenuType.Templates]: templatesRef,
      [CustomizerMenuType.Layout]: layoutRef,
      [CustomizerMenuType.Captions]: captionsRef,
      [CustomizerMenuType.Background]: backgroundRef,
      [CustomizerMenuType.Images]: imagesRef,
      [CustomizerMenuType.Videos]: videosRef,
      [CustomizerMenuType.Audio]: audioRef,
      [CustomizerMenuType.Overlays]: overlaysRef,
      [CustomizerMenuType.Versioning]: versioningRef
    }),
    []
  );
  const closePopup = useCallback(() => {
    setIsMenuPopupOpened(null);
  }, []);

  const openPopupMenu = useCallback(
    (menuType: CustomizerMenuType) => {
      isMenuPopupOpened !== menuType ? setIsMenuPopupOpened(menuType) : closePopup();
    },
    [closePopup, isMenuPopupOpened]
  );

  const isAudioOverlayEnabled = useMemo(() => {
    return featureFlags[FeatureFlagKeys.Use_CL_Timeline_Add_Menu];
  }, [featureFlags]);

  const isOverlaysGroupingEnabled = useMemo(() => {
    return featureFlags[FeatureFlagKeys.Use_CL_Overlays_Grouping];
  }, [featureFlags]);

  const isSplitsTranslationEnabled = useMemo(() => {
    return featureFlags[FeatureFlagKeys.Use_CL_Splits_Translations];
  }, [featureFlags]);

  const isCaptionTranslationEnabledForClip = useMemo(() => {
    return (
      isSplitsTranslationEnabled ||
      !isSplitsInTimelineEnabledPure({
        clip: clipData,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      })
    );
  }, [clipData, isSplitsTranslationEnabled]);

  const { isCaptionTranslationEnabled } = useMemo(() => {
    return {
      // Duplicate recordings are edited like clips (isFullRecordingEdit is false for them), but
      // translation isn't supported on a duplicate, so keep it disabled regardless of that flag.
      isCaptionTranslationEnabled:
        !isFullRecordingEdit && !getIsDuplicateRecording(clipData) && isCaptionTranslationEnabledForClip
    };
  }, [isFullRecordingEdit, clipData, isCaptionTranslationEnabledForClip]);

  const isAudioControlsEnabled = useMemo(() => {
    return featureFlags[FeatureFlagKeys.Use_CL_Audio_Controls];
  }, [featureFlags]);

  const isVersioningEnabled = useMemo(() => {
    return featureFlags[FeatureFlagKeys.Use_CL_Clip_Versioning];
  }, [featureFlags]);

  const shouldTemplatesBeHidden = false;

  const buttons: ActionButton[] = useMemo(() => {
    return [
      {
        action: () => handleAction(),
        icon: 'IconLanguage',
        label: isTranslationProcessing ? 'Translating' : 'Translate',
        trackingId: 'caption-translation-button',
        disabled: isTranslationProcessing || isBlocked('clip-language-switch'),
        isActive: isMenuPopupOpened === CustomizerMenuType.Translation,
        hidden: !isCaptionTranslationEnabled,
        isLoading: isTranslationProcessing,
        isLocked: !allowCaptionTranslation,
        buttonRef: buttonRefs[CustomizerMenuType.Translation],
        showWarning:
          clipCustomizerWarnings[CustomizerMenuType.Translation].showWarning ||
          (allowCaptionTranslation && !isOriginalLanguageSupportedForTranslation),
        warnings: clipCustomizerWarnings[CustomizerMenuType.Translation].warnings
      },
      {
        action: () => openPopupMenu(CustomizerMenuType.Templates),
        icon: 'IconTemplate',
        label: 'Templates',
        trackingId: 'edit-clip-templates-button',
        disabled: isBlocked('customizer-templates'),
        isActive: isMenuPopupOpened === CustomizerMenuType.Templates,
        hidden: shouldTemplatesBeHidden,
        buttonRef: buttonRefs[CustomizerMenuType.Templates],
        showWarning: clipCustomizerWarnings[CustomizerMenuType.Templates].showWarning,
        warnings: clipCustomizerWarnings[CustomizerMenuType.Templates].warnings
      },
      {
        action: () => openPopupMenu(CustomizerMenuType.Layout),
        icon: 'IconLayoutDashboard',
        label: 'Layout',
        trackingId: 'edit-clip-layout-button',
        disabled: isBlocked('customizer-layout'),
        isActive: isMenuPopupOpened === CustomizerMenuType.Layout,
        buttonRef: buttonRefs[CustomizerMenuType.Layout],
        showWarning: clipCustomizerWarnings[CustomizerMenuType.Layout].showWarning,
        warnings: clipCustomizerWarnings[CustomizerMenuType.Layout].warnings
      },
      {
        action: () => openPopupMenu(CustomizerMenuType.Captions),
        icon: 'IconCustomizerCaptions',
        label: 'Captions',
        trackingId: 'edit-clip-captions-button',
        disabled: isBlocked('customizer-captions'),
        isActive: isMenuPopupOpened === CustomizerMenuType.Captions,
        buttonRef: buttonRefs[CustomizerMenuType.Captions],
        showWarning: clipCustomizerWarnings[CustomizerMenuType.Captions].showWarning,
        warnings: clipCustomizerWarnings[CustomizerMenuType.Captions].warnings
      },
      {
        action: () => openPopupMenu(CustomizerMenuType.Background),
        icon: 'IconBackground',
        label: 'Background',
        trackingId: 'edit-clip-background-button',
        disabled: isBlocked('customizer-background'),
        isActive: isMenuPopupOpened === CustomizerMenuType.Background,
        buttonRef: buttonRefs[CustomizerMenuType.Background],
        showWarning: clipCustomizerWarnings[CustomizerMenuType.Background].showWarning,
        warnings: clipCustomizerWarnings[CustomizerMenuType.Background].warnings
      },
      {
        action: () => openPopupMenu(CustomizerMenuType.Images),
        icon: 'IconPhoto',
        label: 'Images',
        trackingId: 'edit-clip-images-button',
        disabled: isBlocked('customizer-images'),
        isActive: isMenuPopupOpened === CustomizerMenuType.Images,
        hidden: isOverlaysGroupingEnabled,
        isLoading: isAddingImage,
        buttonRef: buttonRefs[CustomizerMenuType.Images],
        showWarning: clipCustomizerWarnings[CustomizerMenuType.Images].showWarning,
        warnings: clipCustomizerWarnings[CustomizerMenuType.Images].warnings
      },
      {
        action: () => openPopupMenu(CustomizerMenuType.Videos),
        icon: 'IconCustomizerVideo',
        label: 'Videos',
        trackingId: 'edit-clip-videos-button',
        disabled: isBlocked('customizer-videos'),
        isActive: isMenuPopupOpened === CustomizerMenuType.Videos,
        hidden: isOverlaysGroupingEnabled,
        isLoading: isAddingVideo,
        buttonRef: buttonRefs[CustomizerMenuType.Videos],
        showWarning: clipCustomizerWarnings[CustomizerMenuType.Videos].showWarning,
        warnings: clipCustomizerWarnings[CustomizerMenuType.Videos].warnings
      },
      {
        action: () => openPopupMenu(CustomizerMenuType.Overlays),
        icon: 'IconPhotoVideo',
        label: 'Overlays',
        trackingId: 'edit-clip-overlays-button',
        disabled: isBlocked('customizer-overlays'),
        isActive: isMenuPopupOpened === CustomizerMenuType.Overlays,
        hidden: !isOverlaysGroupingEnabled,
        isLoading: isAddingImage || isAddingVideo,
        buttonRef: buttonRefs[CustomizerMenuType.Overlays],
        showWarning: clipCustomizerWarnings[CustomizerMenuType.Overlays].showWarning,
        warnings: clipCustomizerWarnings[CustomizerMenuType.Overlays].warnings
      },
      {
        action: () => openPopupMenu(CustomizerMenuType.Audio),
        icon: isAudioControlsEnabled ? 'IconCustomizerMusic' : 'IconCustomizerAudio',
        label: isAudioControlsEnabled ? 'Music' : 'Audio',
        trackingId: 'edit-clip-audio-button',
        disabled: isBlocked('customizer-audio'),
        isActive: isMenuPopupOpened === CustomizerMenuType.Audio,
        hidden: !isAudioOverlayEnabled,
        isLoading: isAddingAudio,
        buttonRef: buttonRefs[CustomizerMenuType.Audio],
        showWarning: clipCustomizerWarnings[CustomizerMenuType.Audio].showWarning,
        warnings: clipCustomizerWarnings[CustomizerMenuType.Audio].warnings
      },
      {
        action: () => openPopupMenu(CustomizerMenuType.Versioning),
        icon: 'IconHistory',
        label: 'Versioning',
        trackingId: 'edit-clip-versioning-button',
        disabled: isBlocked('customizer-versioning'),
        isActive: isMenuPopupOpened === CustomizerMenuType.Versioning,
        hidden: !isVersioningEnabled,
        buttonRef: buttonRefs[CustomizerMenuType.Versioning],
        showWarning: false,
        warnings: {}
      }
    ];
  }, [
    isMenuPopupOpened,
    isAudioControlsEnabled,
    isTranslationProcessing,
    isCaptionTranslationEnabled,
    allowCaptionTranslation,
    isOriginalLanguageSupportedForTranslation,
    shouldTemplatesBeHidden,
    isAddingImage,
    isAddingVideo,
    isAudioOverlayEnabled,
    isAddingAudio,
    isOverlaysGroupingEnabled,
    isVersioningEnabled,
    isBlocked,
    openPopupMenu,
    handleAction,
    buttonRefs,
    clipCustomizerWarnings
  ]);

  const popupMenu = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMenuPopupOpened) {
      return;
    }
    function handleOutsideClick(e: MouseEvent) {
      if (isAnyDialogOpened()) {
        return;
      }
      if (!popupMenu.current?.contains(e.target as Node)) {
        closePopup();
      }
    }
    document.addEventListener('pointerdown', handleOutsideClick);
    return () => {
      document.removeEventListener('pointerdown', handleOutsideClick);
    };
  }, [isMenuPopupOpened, closePopup]);

  useEffect(() => {
    if (!isMenuPopupOpened) {
      return;
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && !isAnyDialogOpened()) {
        closePopup();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMenuPopupOpened, closePopup]);

  useEffect(() => {
    const handleOpenCaptionsGallery = () => {
      openPopupMenu(CustomizerMenuType.Captions);
    };

    const eventListener = EventBus.on(CustomEvents.OpenCaptionsGallery, handleOpenCaptionsGallery);
    return () => {
      EventBus.off(CustomEvents.OpenCaptionsGallery, eventListener);
    };
  }, [openPopupMenu]);

  useEffect(() => {
    const handleOpenAudioOverlayLibraryModal = () => {
      openPopupMenu(CustomizerMenuType.Audio);
    };
    const eventListener = EventBus.on(CustomEvents.OpenAudioOverlayLibraryModal, handleOpenAudioOverlayLibraryModal);
    return () => {
      EventBus.off(CustomEvents.OpenAudioOverlayLibraryModal, eventListener);
    };
  }, [openPopupMenu]);

  useEffect(() => {
    const handleOpenVideoOverlayLibraryModal = () => {
      openPopupMenu(CustomizerMenuType.Videos);
    };
    const eventListener = EventBus.on(CustomEvents.OpenVideoOverlayLibraryModal, handleOpenVideoOverlayLibraryModal);
    return () => {
      EventBus.off(CustomEvents.OpenVideoOverlayLibraryModal, eventListener);
    };
  }, [openPopupMenu]);

  useEffect(() => {
    const handleOpenOverlaysPopup = (data?: { tab?: 'video' | 'image' }) => {
      setOverlaysInitialTab(data?.tab ?? 'video');
      openPopupMenu(CustomizerMenuType.Overlays);
    };
    const eventListener = EventBus.on(CustomEvents.OpenOverlaysPopup, handleOpenOverlaysPopup);
    return () => {
      EventBus.off(CustomEvents.OpenOverlaysPopup, eventListener);
    };
  }, [openPopupMenu]);

  // Track if speaker identification popup has been triggered to prevent re-triggering
  const hasTriggeredSpeakerIdentificationRef = useRef(false);

  // Auto-open speaker identification popup when conditions are met on page load
  useEffect(() => {
    // Skip if already triggered (even if closed, don't trigger again on layout change)
    if (hasTriggeredSpeakerIdentificationRef.current) {
      return;
    }

    // Check if feature flag is enabled
    if (!isLayoutsWithVisionDataEnabled(clipData)) {
      return;
    }

    // Check if the layout is a speaker layout
    // Give preference to scenes - if scenes exist and have length > 0, check scenes
    // Otherwise fall back to clip.asset_metadata.layout
    const clipLayout = clipData.asset_metadata.layout;
    const scenes = clipData.asset_metadata.scenes;
    const hasScenes = scenes && scenes.length > 0;

    let hasSpeakerLayout = false;
    if (hasScenes) {
      // If scenes exist, check if any scene has speaker layout
      hasSpeakerLayout = scenes.some(scene => isSpeakerLayout(scene.details.layout));
    } else {
      // If no scenes, fall back to clip layout
      hasSpeakerLayout = isSpeakerLayout(clipLayout);
    }

    if (!hasSpeakerLayout) {
      return;
    }

    // Check if there are warnings for layout
    const hasLayoutWarnings = clipCustomizerWarnings[CustomizerMenuType.Layout].showWarning;
    if (!hasLayoutWarnings) {
      return;
    }

    // All conditions met - trigger speaker identification popup
    hasTriggeredSpeakerIdentificationRef.current = true;
    window.analytics?.track('ContentLabEvent', {
      name: 'SpeakerIdentificationPopupAutoOpened',
      clipId: clipId
    });
    // Defer dispatch to next tick so all React effects in this render cycle
    // (including IdentifySpeakers re-registering its listener) complete first.
    const timeoutId = setTimeout(() => {
      EventBus.dispatch(CustomEvents.OpenSpeakersIdentification, {
        isInlineIdentification: true,
        layout: clipData.asset_metadata.layout
      });
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [clipData, featureFlags, clipCustomizerWarnings, clipId]);

  const onOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      closePopup();
    },
    [closePopup]
  );

  const onCaptionTranslation = useCallback(
    (selectedLanguage: LanguageOption | null) => {
      if (selectedLanguage) {
        onCaptionTranslationConfirm(selectedLanguage);
        closePopup();
      }
    },
    [closePopup, onCaptionTranslationConfirm]
  );

  const showLayoutPopup = useMemo(() => isMenuPopupOpened === CustomizerMenuType.Layout, [isMenuPopupOpened]);

  const isNewLayoutsEnabled = useMemo(() => {
    return !!featureFlags[FeatureFlagKeys.Use_CL_Editor_New_Layouts];
  }, [featureFlags]);

  return (
    <div
      className={
        useNewRecordingsPlayer
          ? 'relative right-[10%] z-[55] flex h-full min-w-[68px] items-start justify-center self-center overflow-y-auto overflow-x-hidden pt-2'
          : 'absolute right-2 z-[55] flex h-auto items-center self-center pl-8'
      }
      ref={popupMenu}
    >
      {isMenuPopupOpened && <div className="fixed right-0 top-0 size-full" onClick={onOverlayClick} />}
      <ActionButtons show={true} buttons={buttons} labelPosition="left" size="medium" />
      {isMenuPopupOpened && (
        <DynamicPopup
          triggerRef={buttonRefs[isMenuPopupOpened]}
          isVisible={!!isMenuPopupOpened}
          preferredPosition="left"
          offset={8}
          viewportPadding={16}
          className={useNewRecordingsPlayer ? 'pointer-events-auto' : undefined}
          style={isMenuPopupOpened && STANDARD_PANEL_MENU_TYPES.has(isMenuPopupOpened) ? { top: 76 } : {}}
        >
          <Transition
            show={!!isMenuPopupOpened}
            enter="transition duration-150 ease-out"
            enterFrom="transform -translate-x-2 scale-[0.98] opacity-0"
            enterTo="transform translate-x-0 scale-1 opacity-100"
            leave="transition duration-100 ease-out"
            leaveFrom="transform translate-x-0 scale-1 opacity-100"
            leaveTo="transform -translate-x-2 scale-[0.98] opacity-0"
          >
            {isMenuPopupOpened === CustomizerMenuType.Captions && (
              <ClipCustomizerCaptionsPopup
                onTimeUpdate={onTimeUpdate}
                onEditCaptions={closePopup}
                isFullRecordingEdit={isFullRecordingEdit}
              />
            )}
            {isMenuPopupOpened === CustomizerMenuType.Templates &&
              (featureFlags[FeatureFlagKeys.Use_CL_Templates_UI_Revamp] ? (
                <ClipCustomizerTemplatesPopupRevamp isFullRecordingEdit={isFullRecordingEdit} onClose={closePopup} />
              ) : (
                <ClipCustomizerTemplatesPopup isFullRecordingEdit={isFullRecordingEdit} />
              ))}
            {showLayoutPopup ? (
              isNewLayoutsEnabled ? (
                <ClipCustomizerNewLayoutPopup
                  warnings={clipCustomizerWarnings[CustomizerMenuType.Layout].warnings}
                  onClose={closePopup}
                />
              ) : (
                <ClipCustomizerLayoutPopupV2 warnings={clipCustomizerWarnings[CustomizerMenuType.Layout].warnings} />
              )
            ) : null}
            {isMenuPopupOpened === CustomizerMenuType.Background && <ClipCustomizerBackgroundPopup />}
            {isMenuPopupOpened === CustomizerMenuType.Speakers && <ClipCustomizerSpeakersPopup />}
            {isMenuPopupOpened === CustomizerMenuType.Translation && (
              <CaptionLanguageSelectorPopup
                closePopup={closePopup}
                onCaptionTranslation={onCaptionTranslation}
                clipData={clipData}
              />
            )}
            {isMenuPopupOpened === CustomizerMenuType.Images && <ClipCustomizerImagesPopup closePopup={closePopup} />}
            {isMenuPopupOpened === CustomizerMenuType.Videos && <ClipCustomizerVideoPopup closePopup={closePopup} />}
            {isMenuPopupOpened === CustomizerMenuType.Audio && <ClipCustomizerAudioPopup closePopup={closePopup} />}
            {isMenuPopupOpened === CustomizerMenuType.Overlays && (
              <ClipCustomizerOverlaysPopup closePopup={closePopup} initialTab={overlaysInitialTab} />
            )}
            {isMenuPopupOpened === CustomizerMenuType.Versioning && (
              <ClipCustomizerVersioningPanel onClose={closePopup} />
            )}
          </Transition>
        </DynamicPopup>
      )}

      <UnsupportedLanguageWarningModal isOpen={isWarningModalOpen} onClose={closeWarningModal} />
    </div>
  );
}

export default memo(ClipCustomizerMenu);
